import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IndexCardsStore } from '../indexcards.store';
import { normalizeName } from '../normalize-name';
import { ConfirmService } from '../../shared/confirm-dialog/confirm-dialog.service';

@Component({
    selector: 'app-medarbejdere-grupper-modal-ui',
    templateUrl: './medarbejdere-grupper-modal.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MedarbejdereGrupperModal {
    @ViewChild('nameInput') nameInput?: ElementRef<HTMLInputElement>;
    private readonly confirmService = inject(ConfirmService);
    readonly indexCardsStore = inject(IndexCardsStore);
    readonly name = signal('');
    readonly busy = signal(false);
    readonly error = signal('');
    readonly normalizedName = computed(() => normalizeName(this.name()));
    readonly unavailable = computed(() => this.busy() || this.indexCardsStore.loading() || this.indexCardsStore.loadError());
    readonly items = computed(() => [...this.indexCardsStore.grupper()]
        .sort((a, b) => (a.gruppe ?? '').localeCompare(b.gruppe ?? '', 'da'))
        .map(item => ({ ...item, employeeCount: this.employeeCount(item.id) })));
    readonly duplicate = computed(() => this.indexCardsStore.grupper().some(item =>
        normalizeName(item.gruppe).toLocaleLowerCase('da') === this.normalizedName().toLocaleLowerCase('da')));

    constructor(private readonly activeModal: NgbActiveModal) {}

    private employeeCount(id: number): number {
        return this.indexCardsStore.users().filter(user => {
            const ids: string[] = user.grupper?.match(/\d+/g) ?? [];
            return ids.includes(String(id));
        }).length;
    }

    onDismiss(): void {
        if (!this.busy()) { this.activeModal.close(); }
    }

    async deleteInstance(item: { id: number; gruppe: string }): Promise<void> {
        if (this.unavailable()) { return; }
        this.busy.set(true);
        this.error.set('');
        try {
            const count = this.employeeCount(item.id);
            const consequence = count
                ? `Tilknytningen fjernes fra ${count} medarbejdere. Medarbejderne slettes ikke.`
                : 'Denne gruppe har ingen tilknyttede medarbejdere.';
            const confirmed = await this.confirmService.question(
                `Slet “${item.gruppe}”? ${consequence}`, 'Slet gruppe', 'Annuller');
            if (confirmed && !await this.indexCardsStore.deleteGruppe(item.id)) {
                this.error.set('Gruppen kunne ikke slettes. Prøv igen.');
            }
        } finally { this.busy.set(false); }
    }

    async newInstance(): Promise<void> {
        if (this.unavailable() || !this.normalizedName() || this.duplicate()) { return; }
        this.busy.set(true);
        this.error.set('');
        try {
            if (await this.indexCardsStore.createGruppe(this.normalizedName())) {
                this.name.set('');
            } else {
                this.error.set('Gruppen kunne ikke oprettes. Navnet er bevaret; prøv igen.');
            }
        } finally {
            this.busy.set(false);
            // Restore focus after Angular removes the disabled state.
            setTimeout(() => this.nameInput?.nativeElement.focus());
        }
    }
}
